/**
 * @description Basic page for when a user has not verified their account yet.
 */

import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Mail, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/axios";
import { useApp } from "@/contexts/AppContext";

interface VerifyAccountProps {
  navigate: (page: string) => void;
}

type ResendVerificationResponse = {
  sent: boolean;
  retryAfterSeconds: number;
};

export default function VerifyAccount({ navigate }: VerifyAccountProps) {
  const { t } = useApp();
  const state = window.history.state?.usr as { email?: string } | undefined;
  const email = state?.email?.trim() ?? "";

  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const pageStyle = {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, var(--background)), color-mix(in srgb, var(--surface) 80%, var(--background)))",
  } as const;
  const iconBadgeStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 18%, var(--background))",
  } as const;
  const iconStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const infoPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--foreground) 6%, var(--surface))",
    borderColor:
      "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, var(--background))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const mutedPrimaryStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;

  const interpolateEmail = (value: string) =>
    value.replace(/{{\s*email\s*}}/g, email || "");

  const isCooldownActive = cooldownSeconds > 0;

  useEffect(() => {
    if (!isCooldownActive) {
      return;
    }

    const timer = window.setInterval(() => {
      setCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isCooldownActive]);

  const formatCountdown = (totalSeconds: number) => {
    const safeSeconds = Math.max(0, totalSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const resendButtonLabel = isLoading
    ? t("auth.verifyAccountResendLoading")
    : cooldownSeconds > 0
      ? t("auth.verifyAccountResendButtonCooldown").replace(
          /{{\s*time\s*}}/g,
          formatCountdown(cooldownSeconds)
        )
      : t("auth.verifyAccountResendButton");

  const helperText =
    t("auth.verifyAccountResendHint");

  const handleResend = async () => {
    if (!email || isLoading || cooldownSeconds > 0) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post<ResendVerificationResponse>("/auth/resend-verification", {
        email,
      });
      const nextCooldown = Math.max(0, Number(response.data?.retryAfterSeconds ?? 0));
      setCooldownSeconds(nextCooldown);

      if (response.data?.sent) {
        toast.success(t("auth.verifyAccountResendSuccess"));
      } else if (nextCooldown > 0) {
        toast.info(
          t("auth.verifyAccountResendWaiting").replace(
            /{{\s*time\s*}}/g,
            formatCountdown(nextCooldown)
          )
        );
      }
    } catch (error) {
      console.error("Failed to resend verification email:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={pageStyle}
    >
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div
              className="mx-auto p-4 rounded-full w-fit mb-4"
              style={iconBadgeStyle}
            >
              <Mail className="w-8 h-8" style={iconStyle} />
            </div>
            <CardTitle className="card-title-text">
              {t("auth.verifyAccountTitle")}
            </CardTitle>
            <CardDescription className="card-description-text">
              {t("auth.verifyAccountSubtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div
              className="p-4 rounded-lg text-sm border space-y-2"
              style={{ ...infoPanelStyle, ...secondaryTextStyle }}
            >
              <p>
                {interpolateEmail(t("auth.verifyAccountBody"))}
              </p>
              <p>{t("auth.verifyAccountNextStep")}</p>
              <p style={mutedPrimaryStyle}>{t("auth.verifyAccountSpamHint")}</p>
            </div>

            {email && (
              <div className="space-y-2">
                <Button
                  onClick={handleResend}
                  variant="outline"
                  className="w-full gap-2"
                  disabled={isLoading || cooldownSeconds > 0}
                >
                  <Send className="w-4 h-4" />
                  {resendButtonLabel}
                </Button>
                {cooldownSeconds === 0 && (
                  <p className="text-xs text-center" style={secondaryTextStyle}>
                    {helperText}
                  </p>
                )}
              </div>
            )}

            <Button
              variant="ghost"
              onClick={() => navigate("login")}
              className="w-full gap-2 text-secondary"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common.back")}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
